import {
  AmountCell,
  Button,
  DateCell,
  Icon,
  Inline,
  Skeleton,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  CompensationConfigRecurrence,
  CompensationSource,
  type CompensationVariantPayment,
} from '@omnipresentgroup/types';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import {
  CompensationSourceTag,
  DeleteVariantPaymentModal,
  EditContractualVariantPaymentModal,
  VariantPaymentState,
  VariantPaymentTooltip,
} from '../components';
import { ColumnHeaders } from '../types';

const dateSortingFunction = (dateA?: string, dateB?: string) => {
  return (
    (dateA ? DateTime.fromISO(dateA).toMillis() : 0) -
    (dateB ? DateTime.fromISO(dateB).toMillis() : 0)
  );
};

export const useVariantPaymentsTableConfig = () => {
  const columnHelper = useMemo(
    () => createColumnHelper<CompensationVariantPayment>(),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('employeeName', {
        cell: (info) => info.getValue(),
        header: () => ColumnHeaders.employeeName,
      }),
      columnHelper.accessor('name', {
        cell: (info) => info.getValue(),
        header: () => ColumnHeaders.type,
      }),
      columnHelper.display({
        id: 'reasonNotes',
        cell: (info) => (
          <VariantPaymentTooltip variantPayment={info.row.original} />
        ),
        header: () => ColumnHeaders.reasonNotes,
        enableSorting: false,
        size: 16,
        meta: {
          loader: <Skeleton height={16} width={16} circle />,
        },
      }),
      columnHelper.accessor('source', {
        cell: (info) => <CompensationSourceTag source={info.getValue()} />,
        header: () => ColumnHeaders.source,
      }),
      columnHelper.accessor('recurrence', {
        cell: (info) => toSentenceCase(`${info.getValue()}`),
        header: () => ColumnHeaders.recurrence,
      }),
      columnHelper.accessor('amount.amount', {
        cell: (info) => (
          <AmountCell
            amount={info.getValue()}
            currency={info.row.original.amount?.currency}
          />
        ),
        header: () => ColumnHeaders.amount,
        sortingFn: (rowA, rowB) => {
          return (
            Number(rowA.original.amount.amount) -
            Number(rowB.original.amount.amount)
          );
        },
      }),
      columnHelper.accessor('effectiveDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.effectiveDate,
        sortingFn: (rowA, rowB) =>
          dateSortingFunction(
            rowA.original.effectiveDate,
            rowB.original.effectiveDate,
          ),
      }),
      columnHelper.accessor('endDate', {
        cell: (info) => {
          const endDate = info.getValue();
          if (
            !endDate &&
            info.row.original.recurrence !==
              CompensationConfigRecurrence.ONE_OFF
          ) {
            return 'Contract end';
          }
          return <DateCell value={endDate} />;
        },
        header: () => ColumnHeaders.endDate,
        sortingFn: (rowA, rowB) =>
          dateSortingFunction(rowA.original.endDate, rowB.original.endDate),
      }),
      columnHelper.accessor('state', {
        cell: (info) => (
          <VariantPaymentState variantPayment={info.row.original} />
        ),
        header: () => ColumnHeaders.state,
      }),
      columnHelper.accessor('isMandatory', {
        cell: (info) =>
          info.getValue() ? (
            <Inline align="center">
              <Icon icon="Check" size="16" />
            </Inline>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          ),
        header: () => ColumnHeaders.isMandatory,
        enableSorting: false,
        size: 70,
        meta: {
          loader: <Skeleton height={16} width={16} circle />,
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) =>
          info.row.original.source === CompensationSource.CONTRACTUAL && (
            <Inline gap="8">
              <EditContractualVariantPaymentModal
                key={`edit-${info.row.original.id}`}
                trigger={<Button variant="tertiary" icon="Edit" size="small" />}
                variantPayment={info.row.original}
              />
              <DeleteVariantPaymentModal
                key={`delete-${info.row.original.id}`}
                trigger={
                  <Button
                    variant="tertiary"
                    icon="Trash"
                    size="small"
                    disabled={info.row.original.isMandatory}
                  />
                }
                variantPayment={info.row.original}
              />
            </Inline>
          ),
        header: () => ColumnHeaders.actions,
        size: 40,
        enableSorting: false,
        meta: {
          onClickStopPropagation: true,
          loader: (
            <Inline gap="8">
              <Skeleton height={24} width={24} />
              <Skeleton height={24} width={24} />
            </Inline>
          ),
        },
      }),
    ],
    [columnHelper],
  );

  return { columns };
};
